import { api } from '@/lib/apiClient'
import type { Envelope } from '@/lib/apiTypes'
import { withCaptcha } from '@/lib/captcha'
import type { AuthSession, SignupRequest, Subscriber } from './types'

// New email → 201 with a session (user lands logged in). Existing email → 200
// `login_required` (no session, a login code was already sent, redirect to login).
export type SignupResult =
  | { kind: 'session'; session: AuthSession }
  | { kind: 'loginRequired'; email: string }

type LoginRequired = { status: 'login_required'; email: string; message: string }
type SignupResponse = AuthSession | LoginRequired

export async function signup(body: SignupRequest): Promise<SignupResult> {
  const res = await withCaptcha((config) =>
    api.post<Envelope<SignupResponse>>('/auth/signup', body, config),
  )
  const data = res.data.data
  if ('status' in data && data.status === 'login_required') {
    return { kind: 'loginRequired', email: data.email }
  }
  return { kind: 'session', session: data as AuthSession }
}

// Passwordless login step 1 + resend code. Sends a one-time code to the email.
export async function requestCode(email: string): Promise<void> {
  await withCaptcha((config) => api.post('/auth/request-code', { email }, config))
}

// Passwordless step 2 — verify the OTP. Serves signup confirmation AND login.
export async function verifyCode(email: string, code: string): Promise<AuthSession> {
  const res = await withCaptcha((config) =>
    api.post<Envelope<AuthSession>>('/auth/verify-code', { email, code }, config),
  )
  return res.data.data
}

// Magic link "one-click" counterpart of verify-code. Consumes the signed token
// from the email link (?token=) and opens a session. Single-use.
export async function magicLogin(token: string): Promise<AuthSession> {
  const res = await withCaptcha((config) =>
    api.post<Envelope<AuthSession>>('/auth/magic-login', { token }, config),
  )
  return res.data.data
}

export async function refreshSession(refreshToken: string): Promise<AuthSession> {
  const res = await api.post<Envelope<AuthSession>>('/auth/refresh', { refreshToken })
  return res.data.data
}

export async function logout(refreshToken: string): Promise<void> {
  await api.post('/auth/logout', { refreshToken })
}

export async function fetchCurrentSubscriber(): Promise<Subscriber> {
  const res = await api.get<Envelope<Subscriber>>('/auth/me')
  return res.data.data
}
